import React, { useEffect, useMemo, useState } from 'react';
import { getLocale, useIntl, useSelector, shallowEqual } from 'umi';
import { Card, Text, Table, Bubble, Row, Col } from "tea-component";
import moment from 'moment';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useSelectorMap, useWeekDay } from '@/pages/dashboard/utils/customHook';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { exportExcel, formatNum, formatExeclDecimal, formatPercentNum, formatExeclNum, formatExeclSharePercent, getIsErrorValue, getRatioText, jumpDetail, formatExcelString } from '@/pages/dashboard/utils/utils';
import { getDateFormatString, hasOnly } from '../_config/common';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import PieChart from '@/pages/dashboard/components/Chart/components/PieChart';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug';
import "@/pages/dashboard/assets/scss/page/Overlay/components/tableCard.scss";
import MapEchart from '@/pages/dashboard/components/MapEchart';
import { getMonetUnit } from '@/pages/dashboard/utils/moneyUnit';
import countries from "@/pages/dashboard/components/MapEchart/_config/countries.json";
import { getSystemFlag } from '@/pages/dashboard/pages/overview/_config/system';
import getSystemLegend from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils';
import { tableInt } from '@/pages/dashboard/pages/overview/components/PcCountriesContext';
import ACard from '@/pages/dashboard/components/ACard';

const {
  scrollable,
} = Table.addons;

export default function CountriesContext(props: any) {
  const { index, data, regionDataType, activeUserType = 'DAU', type, gameCode, formValue } = props;
  const { getSelectorText, getFilterItemText } = useSelectorMap();
  const isDauWauMau = type === 'DAU' || type === 'MAU' || type === 'WAU';
  const { timeType } = formValue;
  const { componentData, loading, error } = data || {};
  const { selector } = useSelector((state: any) => ({
    selector: state?.dashboardSetting?.selector
  }), shallowEqual);
  const locale = getLocale();
  const { formatMessage } = useIntl(); // 中英切换方法
  // 右侧图标
  const iconList = regionDataType === 'country' ? ['barChart', 'lineChart', 'pieChart', 'mapChart'] : ['barChart', 'lineChart', 'pieChart'];
  const iconTypeObj: any = {
    barChart: 'column-chart',
    lineChart: 'discount-chart',
    pieChart: 'pie-chart-icon',
    mapChart: 'chart-map',
  };
  const init = (regionDataType === 'country' && isDauWauMau) ? iconList[3] : iconList[0];
  // 控制图表和数据表格显示
  const [isLine, setIsLine] = useState(init);
  const { getWeekDayStr, getWeekDay } = useWeekDay();
  const PUBLIC_HEIGHT = 390;
  const INTEGER_INDEX = [0, 2];

  // const { rangeTime } = useFilterData(PAGE_ID_MAP[activeUserType]);
  // country和region才有世界地图
  useEffect(() => {
    if (regionDataType !== 'country' && isLine === 'mapChart') {
      setIsLine(iconList[0]);
    }
  }, [regionDataType, isLine]);

  // 获取Country Area Zone区域表格的配置
  const getTableOption = (index: number, type: string, unit: string, formValue: { filterValue: { rangeTime: string[] | undefined; }; timeType: any; }) => {
    const timeType = activeUserType === 'DAU' ? 'overview_daily_DAU' : activeUserType === 'WAU' ? 'overview_weekly_WAU' : 'overview_monthly_MAU';
    const bool = hasOnly(formValue?.filterValue?.rangeTime, formValue.timeType);
    return [
      {
        key: 'country',
        header: formatMessage({ id: `overview_area_tab_${type}` }),
        render: (t: { name: string; full_name: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | null | undefined; }) => {
          let ImgDom: JSX.Element;
          const isOther = t.name === 'Others';
          try {
            if (type === 'os') {
              ImgDom = <img className='country-icon' src={getSystemFlag(t?.full_name)} alt='flag' />;
            } else {
              ImgDom = t?.name?.toLowerCase()?.trim() === 'others' ? <></> :
                <img className='country-icon' src={getCountryFlag(t?.name)} alt="flag" />;
            }
          } catch (error) {
            ImgDom = type === 'country' ? <img className='country-icon' src={getCountryFlag('undefined')} alt="flag" /> : <></>;
          }

          return (
            <div className='flex-item'>
              {(['country', 'os'].includes(type) && !isOther) && ImgDom}
              <Text className="country-text">{(t?.full_name || t?.full_name !== null) ? t?.full_name : '-'}</Text>
            </div>
          );
        },
      },
      {
        key: [`average_${activeUserType?.toLowerCase()}`, 'sum_revenue', 'sum_new'][index],
        header: [
          formatMessage({ id: `overview_area_active_table_header2${bool ? '' : '_avg'}` }, { timeType: formatMessage({ id: timeType }).slice(0, 1) }),
          `${formatMessage({ id: `overview_area_revenue_table_header2${bool ? '' : '_sum'}` })}(${getMonetUnit()})`,
          formatMessage({ id: `overview_area_new_table_header2${bool ? '' : '_sum'}` }),
        ][index],
        align: "right",
        // width: 120,
        render: (t: { change: string | number; value: string | number; }) => {
          const { text, status } = getRatioText(t?.change, false);
          const value = unit ? formatNum(t?.value, true, false, unit) : formatNum(t?.value, false, true, unit);
          const isAllNUll = ((status === 'zero' || status === '') && value === '-');
          return (
            <div className='register-table-daily'>
              {
                isAllNUll ? <div className='register-table-daily-grey'>-</div> :
                  <>
                    <div className="register-table-daily-textsum">{value}</div>
                    <div className={
                      status === 'down' ?
                        'register-table-daily-red'
                        : status === 'zero' || status === '' ? 'register-table-daily-grey' : 'register-table-daily-green'}>{text}
                      {
                        status === '' ? '' : status === 'zero' ? <div className='register-table-daily-line' /> : <SvgIcon type={status === 'down' ? 'red-down-new' : 'green-up-new'} className='register-table-daily-icon' />
                      }
                    </div>

                  </>
              }

            </div>
          );
        },
      },
      {
        key: 'share',
        align: "right",
        header: formatMessage({ id: 'overview_area_active_table_header3' }),
        // width: 120,
        render: (t: { share_of_all: string; }) => {
          let share_of_all = '-';
          if (getIsErrorValue(t?.share_of_all)) {
            share_of_all = '-';
          } else {
            share_of_all = formatPercentNum(parseFloat(t.share_of_all.trim()), 1);
          }
          return (<span>{share_of_all}</span>);
        },
      }
    ];
  };
  const componentConfig = useMemo(() => {
    switch (type) {
      case 'DAU':
        return {
          title: formatMessage({ id: `overview_monthly_${activeUserType}` }),
          tableData: {
            columns: getTableOption(0, regionDataType, '', formValue),
            records: componentData?.dau?.dau_list,
          },
          chartData: componentData?.dau?.dau_date_list,
          indicators: activeUserType?.toLowerCase(),
          formValue,
        };
      case 'Revenue':
        return {
          title: formatMessage({ id: 'overview_Revenue' }),
          tableData: {
            columns: getTableOption(1, regionDataType, getMonetUnit(), formValue),
            records: componentData?.revenue?.revenue_list,
          },
          chartData: componentData?.revenue?.revenue_date_list,
          unit: getMonetUnit(),
          indicators: 'revenue',
          formValue,
        };
      case 'New':
        return {
          title: formatMessage({ id: 'overview_New' }),
          tableData: {
            columns: getTableOption(2, regionDataType, '', formValue),
            records: componentData?.new?.new_list,
          },
          chartData: componentData?.new?.new_date_list,
          indicators: 'new',
          formValue,
        };
      default:
        return {
          title: formatMessage({ id: 'overview_daily_DAU' }),
          tableData: {
            columns: getTableOption(0, regionDataType, '', formValue),
            records: componentData?.dau?.dau_list,
          },
          chartData: componentData?.dau?.dau_date_list,
          indicators: 'dau',
          formValue,
        };
    }
  }, [data, activeUserType, regionDataType, formValue, type, locale]);
  const { chartData: chartDataProps = [], tableData: tableDataProps = [] } = componentConfig;
  const hasOthersValue: boolean = tableDataProps?.records?.find((item: { name: string; }) => item?.name === 'Others')?.value !== '';
  // 特殊处理system来源others显示
  const handlePcOsOthers = (handleData: Array<tableInt>, key = 'name') => (handleData?.length >= 11 || hasOthersValue) ? handleData : handleData?.filter(v => (v?.[key] !== 'Others'));

  const chartData = useMemo(() => chartDataProps?.map((item: { list: tableInt[]; }) => ({
    ...item,
    list: handlePcOsOthers(item.list, 'full_crz_name')?.map(listItem => ({
      ...listItem,
      full_crz_name: getFilterItemText(regionDataType, listItem?.crz_name, listItem?.full_crz_name)
    }))
  })), [chartDataProps, locale, regionDataType, selector]);


  const tableData = useMemo(() => ({
    ...tableDataProps,
    records: handlePcOsOthers(tableDataProps?.records)?.map(record => ({
      ...record,
      full_name: getFilterItemText(regionDataType, record?.name, record?.full_name)
    }))
  }), [tableDataProps, locale, regionDataType, selector]);

  const legendDataList = chartData?.[0]?.list || [];
  const legendData = useMemo(() => legendDataList?.map((item: { full_crz_name: any; }) => item.full_crz_name) || [], [legendDataList]);

  const xAxisData = useMemo(() => chartData?.map((item: { date_ymd: string; }) => (activeUserType === 'WAU' ? getDateFormatString('WEEK', item.date_ymd) : item.date_ymd)), [chartData, activeUserType]);

  const dataValueItemArr: any[] = [];
  const dataShareItemArr: any[] = [];

  // 饼图数据
  const dataPieArr = useMemo(
    () =>
      tableData?.records?.map((item) => ({
        value: item.value,
        name: item.full_name,
      })) || [],
    [tableData?.records]
  );
  // 饼图图例
  const legendPieData = useMemo(
    () => tableData?.records?.map((item) => item.full_name) || [],
    [tableData?.records]
  );

  // 扁平化数据展示浮窗
  const shareData = useMemo(() => {
    const shareData = {};
    chartData?.forEach((item: { list: any[]; date_ymd: any; }) => {
      item?.list?.forEach((e: { full_crz_name: any; share: number; }) => {
        shareData[e.full_crz_name + item.date_ymd] = formatPercentNum(e.share, 1);
      });
    });
    return shareData;
  }, [chartData]);

  // 指标值
  const dataValueArr = useMemo(() => {
    for (let i = 0;i < chartData.length;i++) {
      const valueArr: string[] = chartData[i]?.list?.map((item: { value: any; }) => item.value);
      const shareArr: string[] = chartData[i]?.list?.map((item: { share: any; }) => item.share);
      dataValueItemArr.push(valueArr);
      dataShareItemArr.push(shareArr);
    }
    return (
      dataValueItemArr?.[0]?.map((col: any, i: string | number) => dataValueItemArr.map((row) => row[i])) || []
    );
  }, [dataValueItemArr, chartData]);

  // // share数据
  // const dataShareArr = useMemo(() => {
  //   return (
  //     dataShareItemArr?.[0]?.map(function (col, i) {
  //       return dataShareItemArr.map(function (row) {
  //         return row[i];
  //       });
  //     }) || []
  //   );
  // }, [dataShareItemArr]);

  // let dataShareObjArr: any[] = [];

  // dataShareObjArr = dataShareArr?.map((items) => {
  //   let arrItem = items?.map((item, index) => {
  //     return {
  //       time: xAxisData[index],
  //       share: item,
  //     };
  //   });
  //   dataShareObjArr.push(arrItem);
  //   return dataShareObjArr;
  // });

  const barSeries = useMemo(() => dataValueArr.length > 0
    ? dataValueArr?.map((item: any, index: string | number) => ({
      name: legendData[index],
      type: "bar",
      stack: "overviewBar",
      data: item,
      barCategoryGap: '50%',
      cursor: "pointer",
      // areaStyle: {},
    })) : [], [dataValueArr, legendData]);


  const lineSeries = useMemo(() => dataValueArr.length > 0
    ? dataValueArr?.map((item: any, index: string | number) => ({
      name: legendData[index],
      type: "line",
      // stack: "xxx",
      // symbol: "none",
      // areaStyle: {
      //   color: METRICS_COLORS[index],
      // },
      emphasis: {
        focus: "none",
      },
      symbolSize: 4,
      showAllSymbol: true,
      symbol: 'circle',
      data: item,
    }))
    : [], [dataValueArr, legendData]);


  // 柱状图和折线图浮窗展示
  const barFormatterFunc = (param: { axisValue: moment.MomentInput; }[] | { seriesName: any; value: any; color: any; axisValue: any; }[]) => {
    const { unit } = componentConfig;
    return getEchartTooltipText({
      title: getWeekDayStr(moment(param?.[0]?.axisValue).format(timeType?.toLowerCase() === 'MONTH' ? 'YYYY-MM' : 'YYYY-MM-DD'), timeType),
      metricsName: unit ? `${componentConfig.title}(${unit})` : componentConfig.title,
      ratioName: formatMessage({ id: 'overview_area_new_chart_share' }),
      hasRatio: true,
      data: param.map(({ seriesName, value, color, axisValue }) => ({
        name: seriesName,
        icon: regionDataType === 'os' ? getSystemFlag(seriesName) : '',
        metricsNum: formatNum(value, false, INTEGER_INDEX.includes(index)),
        ratioNum: shareData[seriesName + axisValue],
        color,
      })),
      isSort: true
    });
  };

  // 饼图浮窗展示
  const pieFormatterFunc = (param: { name: any; value: any; color: any; data: { total: any; }; }) => {
    const { unit } = componentConfig;
    const { name, value, color, data: { total } } = param;
    const metricsNum = formatNum(value, false, INTEGER_INDEX.includes(index));
    return getEchartTooltipText({
      title: `${getWeekDayStr(formValue?.filterValue?.rangeTime?.[0], timeType)}${formValue?.filterValue?.rangeTime?.[0] !== formValue?.filterValue?.rangeTime?.[1] ? ` ~ ${getWeekDayStr(formValue?.filterValue?.rangeTime?.[1], timeType)}` : ''}`,
      metricsName: '',
      ratioName: '',
      hasRatio: true,
      data: [{
        name,
        icon: regionDataType === 'os' ? getSystemFlag(name) : '',
        metricsNum: metricsNum !== '-' ? unit ? unit + metricsNum : metricsNum : metricsNum,
        ratioNum: formatPercentNum(value / total, 1),
        color
      }]
    });
  };

  // 浮窗展示
  const mapFormatterFunc = (param: any) => {
    const { data, name, value, color, componentSubType } = param;
    // 地图标记的浮窗隐藏掉
    if (!componentSubType) return;
    const { unit } = componentConfig;
    if (!data) return null;
    const metricsName = componentConfig?.tableData?.columns?.[1]?.header;
    return getEchartTooltipText({
      title: formatMessage({ id: `overview_area_tab_${regionDataType}` }),
      metricsName: metricsName ? metricsName : unit ? `${componentConfig.title}(${unit})` : componentConfig.title,
      ratioName: formatMessage({ id: 'overview_area_active_table_header3' }),
      hasRatio: true,
      data: [{
        name: getSelectorText(regionDataType, name),
        icon: regionDataType === 'os' ? getSystemFlag(name) : '',
        metricsNum: formatNum(value, false, INTEGER_INDEX.includes(index)),
        ratioNum: formatPercentNum(parseFloat(data?.share_of_all?.trim()), 1),
        color,
      }]
    });
  };

  const title = formatMessage({ id: `overview_area_title_${regionDataType}` }, { timeType: componentConfig?.title });
  // 下载文件
  const downloadDataFunc = () => {
    // 表格数据
    const excelData: any[] = [];
    // 表头数据
    const regionVal = formatMessage({ id: 'overview_monthly_Dowload_Region' });
    const countryVal = formatMessage({ id: 'overview_monthly_Dowload_country' });
    const shareVal = formatMessage({ id: 'overview_monthly_Dowload_Share' });
    const dateVal = formatMessage({ id: 'overview_monthly_Dowload_Date' });
    const revenueVal = `${formatMessage({ id: 'overview_monthly_Dowload_Revenue' })}(${getMonetUnit()})`;
    const newVal = formatMessage({ id: 'overview_monthly_Dowload_New' });
    const zoneVal = formatMessage({ id: 'overview_monthly_Dowload_Zone' });
    const channelVal = formatMessage({ id: 'overview_monthly_Dowload_Channel' });
    const platFormVal = formatMessage({ id: 'overview_monthly_Dowload_PlatForm' });
    const activeUserTypeObj: any = {
      WAU: formatMessage({ id: 'overview_monthly_WAU' }),
      DAU: formatMessage({ id: 'overview_monthly_DAU' }),
      MAU: formatMessage({ id: 'overview_monthly_MAU' }),
    };
    const regionObj = {
      country: {
        [activeUserType]: {
          countryName: countryVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        Revenue: {
          countryName: countryVal,
          countryValueName: revenueVal,
          share: shareVal,
          date: dateVal,
        },
        New: {
          countryName: countryVal,
          countryValueName: newVal,
          share: shareVal,
          date: dateVal,
        },
      },
      region: {
        [activeUserType]: {
          countryName: regionVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        Revenue: {
          countryName: regionVal,
          countryValueName: revenueVal,
          share: shareVal,
          date: dateVal,
        },
        New: {
          countryName: regionVal,
          countryValueName: newVal,
          share: shareVal,
          date: dateVal,
        },
      },
      zone: {
        [activeUserType]: {
          countryName: zoneVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        Revenue: {
          countryName: zoneVal,
          countryValueName: revenueVal,
          share: shareVal,
          date: dateVal,
        },
        New: {
          countryName: zoneVal,
          countryValueName: newVal,
          share: shareVal,
          date: dateVal,
        },
      },
      channel: {
        [activeUserType]: {
          countryName: channelVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        Revenue: {
          countryName: channelVal,
          countryValueName: revenueVal,
          share: shareVal,
          date: dateVal,
        },
        New: {
          countryName: channelVal,
          countryValueName: newVal,
          share: shareVal,
          date: dateVal,
        },
      },
      os: {
        [activeUserType]: {
          countryName: platFormVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        Revenue: {
          countryName: platFormVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
        New: {
          countryName: platFormVal,
          countryValueName: activeUserTypeObj[activeUserType],
          share: shareVal,
          date: dateVal,
        },
      }
    };
    const title = formatMessage({ id: `overview_area_title_${regionDataType}` }, { timeType: componentConfig?.title });
    let key = componentConfig?.title;

    if (key === formatMessage({ id: 'overview_monthly_MAU' })) {
      key = 'MAU';
    }

    if (key === formatMessage({ id: 'overview_weekly_WAU' })) {
      key = 'WAU';
    }

    if (key === formatMessage({ id: 'overview_monthly_DAU' })) {
      key = 'DAU';
    }
    if (key === formatMessage({ id: 'overview_area_revenue_table_header2' })) {
      key = 'Revenue';
    }
    if (key === formatMessage({ id: 'overview_table_new_users' })) {
      key = 'New';
    }
    const formatFunc = key === 'Revenue' ? formatExeclDecimal : formatExeclNum;

    const table = [
      {
        A: regionObj?.[regionDataType]?.[key]?.date,
        B: formatMessage({ id: 'common_day_of_week' }),
        C: regionObj?.[regionDataType]?.[key]?.countryName,
        D: regionObj?.[regionDataType]?.[key]?.countryValueName,
        E: regionObj?.[regionDataType]?.[key]?.share,
      },
    ];
    for (let i = 0;i < chartData.length;i++) {
      const data = chartData[i]?.list || [];
      data.forEach((item: { date_ymd: any; full_crz_name: any; value: any; share: any; }) => {
        item.date_ymd = timeType == "Month" ? moment(chartData[i].date_ymd).format("YYYY-MM") : chartData[i].date_ymd;
        excelData.push({
          date_ymd: item.date_ymd,
          full_crz_name: item.full_crz_name,
          value: item.value,
          share: item.share,
        });
      });
    }
    excelData.forEach((item) => {
      const row = {
        // A: getWeekDayStr(getDateFormatString(timeType.toUpperCase(), item.date_ymd), timeType),
        A: getIsErrorValue(item.date_ymd) ? '-' : item.date_ymd?.split('(')?.[0],
        B: getIsErrorValue(item.date_ymd) ? '-' : getWeekDay(item.date_ymd),
        C: formatExcelString(item.full_crz_name),
        D: formatFunc(item?.value ? item?.value : ''),
        E: formatExeclSharePercent(item.share),
      };

      table.push(row);
    });
    // const colWidth = [{ wpx: 90 }, { wpx: 110 }, { wpx: 87 }, { wpx: 60 }];
    if (timeType === "Month") {
      //  月粒度删除周的列
      table.map(item => { delete item.B });
    }
    // 导出excel
    exportExcel(title, table);
  };

  const jumptoDetail = () => {
    jumpDetail(`/dashboard/game/${gameCode}/comparisonAnalysis/countryComparison`, formValue, componentConfig?.indicators);
  };

  const worldData = useMemo(() => tableData?.records?.map((item: any) => ({
    ...item,
    name: item?.full_name || item.name,
  })), [tableData]);

  const mapColorV = useMemo(() => {
    const maxValue = tableData?.records?.[0]?.value;
    const interval: number = Math.ceil(maxValue / 6);
    const num = interval?.toString()?.length > 2 ? (10 ** (interval?.toString()?.length - 1)) : 1;
    const newInterval = Math.ceil(interval / num) * num;
    // 计算出色值对应的value
    return interval ? Array.from({ length: 7 }).map((_: unknown, i: number) => (i * newInterval)) : [];
  }, [tableData]);

  const chartComponent = {
    barChart: <BarChart
      height={PUBLIC_HEIGHT}
      key={JSON.stringify(barSeries)}
      legendData={legendData}
      xAxisData={xAxisData || []}
      series={barSeries}
      formatterFunc={barFormatterFunc}
      setOtherConfigFunc={(opt: { xAxis: { axisLabel: { formatter: (value: any) => string; }; }[]; legend: { bottom: number; }; }) => {
        opt.xAxis[0].axisLabel.formatter = function (value: any) {
          return activeUserType === 'MAU' ? `${moment(value).format('YYYY-MM')}` : `${moment(value).format('MM-DD')}`;
        };
        opt = xAxisHighLight(opt, {
          formatter: (value: { value: moment.MomentInput; }) => activeUserType === 'MAU' ? `${moment(value?.value).format('YYYY-MM')}` : `${moment(value?.value).format('MM-DD')}`
        });
        if (regionDataType === 'os') {
          getSystemLegend(opt, selector);
          opt.legend.bottom = -4;
        }
        return opt;
      }}
    />,
    lineChart: <LineChart
      height={PUBLIC_HEIGHT}
      key={JSON.stringify(lineSeries)}
      legendData={legendData}
      xAxisData={xAxisData || []}
      series={lineSeries}
      formatterFunc={barFormatterFunc}
      setOtherConfigFunc={(opt: { xAxis: { axisLabel: { formatter: (value: any) => string; }; }[]; legend: { bottom: number; }; }) => {
        opt.xAxis[0].axisLabel.formatter = function (value: any) {
          return activeUserType === 'MAU' ? `${moment(value).format('YYYY-MM')}` : `${moment(value).format('MM-DD')}`;
        };
        opt = xAxisHighLight(opt, {
          formatter: (value: { value: moment.MomentInput; }) => activeUserType === 'MAU' ? `${moment(value?.value).format('YYYY-MM')}` : `${moment(value?.value).format('MM-DD')}`
        });
        if (regionDataType === 'os') {
          getSystemLegend(opt, selector);
          opt.legend.bottom = -4;
        }
        return opt;
      }}
    />,
    pieChart: <PieChart
      chartData={dataPieArr}
      height={PUBLIC_HEIGHT}
      legendData={legendPieData}
      isShowTop5Lines={true}
      setOtherConfigFunc={(opt: any) => {
        if (regionDataType === 'os') {
          getSystemLegend(opt, selector);
        }
        return opt;
      }}
      formatterFunc={pieFormatterFunc}
    />,
    mapChart: <MapEchart
      height={PUBLIC_HEIGHT}
      isUnit={type === "Revenue"}
      data={worldData}
      mapColorV={mapColorV}
      formatterFunc={mapFormatterFunc}
      setOtherConfigFunc={(opt: any) => {
        try {
          const markData: { name: string; coord: [number, number]; }[] = [];
          for (let i = 0;i < 5;i++) {
            const item = worldData?.[i];
            // 这个判断可以过滤掉 worldData[i]为空, name为其他, 以及找不到coord的情况
            // @ts-ignore
            const coord = countries[getLocale()]?.[item?.name];
            coord && markData.push({ name: item?.name, coord });
          }

          const specialCountries = [
            'United States', '美国',
            'Brazil', '巴西',
            'Canada', '加拿大',
            // 在这里添加其他国家的中英名称
          ];

          opt.series[0].markPoint = {
            symbol: 'path://M10.031 14H4a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4h19a4 4 0 0 1 4 4v6a4 4 0 0 1-4 4H16.97L13.5 18.445 10.031 14Z',
            symbolSize: [27, 18.44],
            animation: false,
            silent: true,
            data: markData.map((item, index) => ({
              name: item.name,
              coord: item.coord,
              itemStyle: {
                color: '#29B569FF',
              },
              symbolOffset: [0, specialCountries.includes(item.name) ? -20 : -10],
              label: {
                show: true,
                formatter: `Top ${index + 1}`,
                position: 'inside',
                color: 'white',
                fontSize: 8,
                fontWeight: 'bold',
                fontStyle: 'italic',
                width: 19,
                height: 10,
                offset: [0, -2],
              },
            })),
          };
          if (regionDataType === 'os') {
            getSystemLegend(opt, selector);
          }
        } catch (e) {
          console.log("Overview页面设置地图前5个国家标注错误: ", e);
        }
        return opt;
      }}
    />
  };

  const infoMessage = {
    barChart: formatMessage({ id: 'common_bar_chart' }),
    lineChart: formatMessage({ id: 'common_line_chart' }),
    pieChart: formatMessage({ id: 'common_pie_chart' }),
    mapChart: formatMessage({ id: 'common_map_chart' }),
  };

  const isNoData = tableData?.records?.length === 0;

  return (
    <>
      <Card className='ovrlay-coumtries'>
        <Card.Body title={
          <ACard url={`/dashboard/game/${gameCode}/comparisonAnalysis/countryComparison`}>
            <div className='overlay-line-title-flex'>
              <div className='dbd-overlay-line-title' onClick={jumptoDetail}>
                <div className='overlay-line-title'>{title}</div>
                <span className='overview-region-title-jump'>
                  <SvgIcon className='overlay-line-title-icon' type='arrow-right'></SvgIcon>
                </span>
              </div>
            </div>
          </ACard>
        }
        operation={!isNoData && <div className='overlay-line-flex'>
          {iconList.map((t) => <Bubble dark placement="top" content={infoMessage[t]} key={t}>
            <Text className='overlay-line-item' onClick={() => { setIsLine(t) }} ><SvgIcon className={isLine !== t ? 'overlay-line-icon' : 'overlay-line-action'} type={iconTypeObj[t]} /></Text>
          </Bubble>)}
          <Bubble dark placement="top" content=''>
            <Text className='' onClick={downloadDataFunc}><SvgIcon className="overlay-line-down" type="download" /></Text>
          </Bubble>
        </div>}>
          <div className='flex-context overall-full-width'>
            <Row className='overall-full-width'>
              <Col span={12}>
                <Abnormal isLoading={loading} isError={error} isNoData={isNoData} style={{ height: 360 }}
                  component={
                    <Table
                      className={`analysis-left ${tableData?.records?.map(record => record.name === 'Others' ? 'FooterFixed' : '').join(' ')}`}
                      records={tableData?.records || []}
                      columns={tableData?.columns || []}
                      addons={[
                        scrollable({
                          maxHeight: 342,
                          // minWidth: 758,
                        }),
                      ]}
                    />
                  }
                />
              </Col>
              <Col span={12} style={{ paddingRight: 0 }}>
                <Row><Col style={{ paddingRight: 0 }}>
                  <div className='table-line-hieght'>
                    <Abnormal isLoading={loading} isError={error} isNoData={isNoData}
                      style={{ height: 360 }}
                      component={chartComponent[isLine]}
                    />
                  </div></Col>
                </Row>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>
    </>
  );
};
